import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import CategoryModal from '../../components/admin/CategoryModal';
import { useToast } from '../../components/common/Toast';
import { categoryService } from '../../services/category.service';
import { theme } from '../../config/theme';
import { TagIcon, EditIcon, TrashIcon, PlusIcon } from '../../components/icons/Icons';

const Categories = () => {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCategories();
  }, [filter]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filter === 'active') filters.is_active = true;
      if (filter === 'inactive') filters.is_active = false;
      if (filter === 'root') filters.parent_id = 'null';

      const response = await categoryService.getAll(filters);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await categoryService.delete(id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setEditingCategory(null);
    if (shouldRefresh) fetchCategories();
  };

  const getCategoryTree = (categories, parentId = null, level = 0) => {
    return categories
      .filter(cat => cat.parent_id === parentId)
      .map(cat => ({
        ...cat,
        level,
        children: getCategoryTree(categories, cat.id, level + 1)
      }));
  };

  const renderCategory = (category) => {
    const hasChildren = category.children && category.children.length > 0;
    
    return (
      <React.Fragment key={category.id}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing.md,
            paddingLeft: `${theme.spacing.md + (category.level * 32)}px`,
            borderBottom: `1px solid ${theme.colors.gray[200]}`,
            backgroundColor: category.level > 0 ? theme.colors.gray[50] : 'transparent',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              {category.level > 0 && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: theme.colors.gray[400] }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              )}
              {category.image && (
                <img 
                  src={category.image} 
                  alt={category.name} 
                  style={{
                    width: '32px',
                    height: '32px',
                    objectFit: 'cover',
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.gray[200]}`
                  }}
                />
              )}
              <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                {category.name}
              </span>
              {!category.is_active && (
                <span style={{ 
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.text.secondary,
                  backgroundColor: theme.colors.gray[100],
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  borderRadius: theme.borderRadius.sm,
                }}>
                  Inactive
                </span>
              )}
            </div>
            {category.description && (
              <div style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                marginTop: theme.spacing.xs,
              }}>
                {category.description}
              </div>
            )}
            <div style={{ 
              fontSize: theme.typography.fontSize.xs,
              color: theme.colors.text.secondary,
              marginTop: theme.spacing.xs,
            }}>
              Slug: {category.slug}
            </div>
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(category)}
              style={{ color: theme.colors.primary.main }}
            >
              <EditIcon size={18} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(category.id, category.name)}
              style={{ color: theme.colors.error }}
            >
              <TrashIcon size={18} />
            </Button>
          </div>
        </div>
        {hasChildren && category.children.map(child => renderCategory(child))}
      </React.Fragment>
    );
  };

  const filterButtonStyles = (active) => ({
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    border: `1px solid ${active ? theme.colors.primary.main : theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    background: active ? theme.colors.primary.main : theme.colors.white,
    color: active ? theme.colors.white : theme.colors.text.primary,
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeight.medium,
    fontSize: theme.typography.fontSize.sm,
    transition: theme.transitions.base,
  });

  if (loading) return <LoadingSpinner fullScreen />;

  const categoryTree = getCategoryTree(categories);

  return (
    <>
      <MainLayout>
        <div style={{ padding: theme.spacing.xl }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.xl }}>
            <div>
              <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.xs }}>
                Categories
              </h1>
              <p style={{ color: theme.colors.text.secondary }}>
                Manage product categories and subcategories
              </p>
            </div>
            <Button 
              variant="primary"
              size="md"
              onClick={handleCreate}
            >
              <PlusIcon size={20} />
              Add Category
            </Button>
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.xl, flexWrap: 'wrap' }}>
            <button style={filterButtonStyles(filter === 'all')} onClick={() => setFilter('all')}>
              All
            </button>
            <button style={filterButtonStyles(filter === 'root')} onClick={() => setFilter('root')}>
              Root Categories
            </button>
            <button style={filterButtonStyles(filter === 'active')} onClick={() => setFilter('active')}>
              Active
            </button>
            <button style={filterButtonStyles(filter === 'inactive')} onClick={() => setFilter('inactive')}>
              Inactive
            </button>
          </div>

          {categories.length === 0 ? (
            <EmptyState
              icon={<TagIcon size={48} />}
              title="No categories found"
              description="Create your first category to get started"
              action={<Button variant="primary" size="md" onClick={handleCreate}>Add Category</Button>}
            />
          ) : (
            <Card padding="none">
              <div>
                {categoryTree.map(category => renderCategory(category))}
              </div>
            </Card>
          )}
        </div>
      </MainLayout>

      {showModal && (
        <CategoryModal
          isOpen={showModal}
          onClose={handleModalClose}
          category={editingCategory}
          allCategories={categories}
        />
      )}
    </>
  );
};

export default Categories;